import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toastMsg } from '../components/common/Toast/Toast';
import {
  NON_CUSTODIAL_CONNECTION_STATUS,
} from '../constants/wallet.constants';
import {
  ATS,
  clearSelectedTradeWallet,
  setActivePairBalance,
  setLinkedWallets,
  setNonCustodialConnectionStatus,
  setSelectedTradeWallet,
} from '../features/ats/atsSlice';
import {
  buildLinkedWalletsSymbol,
  enrichWalletFromLinkedSiblings,
  getLinkedWalletAddress,
  isCustodialWallet,
  isNonCustodialWallet,
} from '../services/wallet/linkedWallet.utils';
import { getNonCustodialProvider } from '../services/wallet/nonCustodialProviderRegistry';
import {
  clearNonCustodialWeb3Session,
  setNonCustodialWeb3Session,
} from '../services/wallet/nonCustodialSession';

export function useTradeWallet({ selectedPair }) {
  const dispatch = useDispatch();
  const linkedWallets = useSelector((state) => state?.ats?.linkedWallets ?? []);
  const linkedWalletsLoading = useSelector(
    (state) => state?.ats?.linkedWalletsLoading,
  );
  const selectedTradeWallet = useSelector(
    (state) => state?.ats?.selectedTradeWallet,
  );
  const nonCustodialConnectionStatus = useSelector(
    (state) => state?.ats?.nonCustodialConnectionStatus,
  );
  const linkedWalletsSymbol = buildLinkedWalletsSymbol(selectedPair);
  const prevLinkedWalletsSymbolRef = useRef(null);

  const resetWalletSelection = useCallback(() => {
    clearNonCustodialWeb3Session();
    dispatch(clearSelectedTradeWallet());
  }, [dispatch]);

  useEffect(() => {
    if (!linkedWalletsSymbol) {
      resetWalletSelection();
      dispatch(setLinkedWallets([]));
      prevLinkedWalletsSymbolRef.current = '';
      return;
    }

    const previousSymbol = prevLinkedWalletsSymbolRef.current;
    // Keep selection across remounts / same-pair refetches (e.g. after place order).
    // Only clear when the trading pair symbol actually changes.
    const pairChanged =
      Boolean(previousSymbol) && previousSymbol !== linkedWalletsSymbol;

    prevLinkedWalletsSymbolRef.current = linkedWalletsSymbol;

    if (pairChanged) {
      resetWalletSelection();
    }

    dispatch(ATS.fetchLinkedWallets(linkedWalletsSymbol));
  }, [dispatch, linkedWalletsSymbol, resetWalletSelection]);

  const selectTradeWallet = useCallback(
    async (wallet) => {
      if (!wallet) return;

      const enrichedWallet = enrichWalletFromLinkedSiblings(wallet, linkedWallets);

      if (isCustodialWallet(enrichedWallet)) {
        clearNonCustodialWeb3Session();
        dispatch(setSelectedTradeWallet(enrichedWallet));
        dispatch(setNonCustodialConnectionStatus(NON_CUSTODIAL_CONNECTION_STATUS.IDLE));
        return;
      }

      if (!isNonCustodialWallet(enrichedWallet)) return;

      dispatch(setActivePairBalance(null));
      dispatch(setNonCustodialConnectionStatus(NON_CUSTODIAL_CONNECTION_STATUS.CONNECTING));
      try {
        const linkedAddress = getLinkedWalletAddress(enrichedWallet);
        const provider = getNonCustodialProvider(enrichedWallet.provider);
        const session = await provider.connect({
          expectedAddress: linkedAddress,
          chainId: enrichedWallet.chainId,
          wallet: enrichedWallet,
        });
        setNonCustodialWeb3Session(session.web3);
        dispatch(setSelectedTradeWallet(enrichedWallet));
        dispatch(setNonCustodialConnectionStatus(NON_CUSTODIAL_CONNECTION_STATUS.CONNECTED));
      } catch (error) {
        clearNonCustodialWeb3Session();
        dispatch(clearSelectedTradeWallet());
        const isMismatch = error?.code === 'WALLET_ADDRESS_MISMATCH';
        dispatch(
          setNonCustodialConnectionStatus(
            isMismatch
              ? NON_CUSTODIAL_CONNECTION_STATUS.MISMATCH
              : NON_CUSTODIAL_CONNECTION_STATUS.ERROR,
          ),
        );
        // Mismatch is shown inline in Select Wallet modal — avoid red toast noise.
        if (!isMismatch) {
          toastMsg.error(error?.message || 'Failed to connect wallet.');
        }
        throw error;
      }
    },
    [dispatch, linkedWallets],
  );

  const isWalletReady =
    Boolean(selectedTradeWallet) &&
    (isCustodialWallet(selectedTradeWallet) ||
      nonCustodialConnectionStatus === NON_CUSTODIAL_CONNECTION_STATUS.CONNECTED);

  const isNonCustodialBlocked =
    Boolean(selectedTradeWallet) &&
    isNonCustodialWallet(selectedTradeWallet) &&
    nonCustodialConnectionStatus !== NON_CUSTODIAL_CONNECTION_STATUS.CONNECTED;

  return {
    linkedWallets,
    linkedWalletsLoading,
    selectedTradeWallet,
    nonCustodialConnectionStatus,
    selectTradeWallet,
    isWalletReady,
    isNonCustodialBlocked,
  };
}

export default useTradeWallet;
